<template>
    <div id="app">
		<notifications group="foo" />
        <router-view/>
    </div>
</template>
<script>
    import '../static/fxView/js/jquery.min.js'
    import '../static/fxView/js/popper.min.js'
    import '../static/fxView/js/bootstrap.min.js'
    import * as types from './types'
    import * as RxFactory from './store/api/rx-factory'
    import * as Enpoints from './store/api/endpoints'

    // browser push notification to admins for every new AP ticket (sent by the server as soon as a ticket is created)
    const AP_TICKET_SW = '/static/ap-ticket-sw.js'

    const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4)
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
        const raw = window.atob(base64)
        return Uint8Array.from([...raw].map(char => char.charCodeAt(0)))
    }

    // resolves once the registration's own service worker is active (needed before pushManager.subscribe)
    const waitForActiveWorker = (reg) => {
        if(reg.active){
            return Promise.resolve(reg)
        }
        const worker = reg.installing || reg.waiting
        if(!worker){
            return Promise.reject(new Error('no service worker'))
        }
        return new Promise((resolve, reject) => {
            worker.addEventListener('statechange', () => {
                if(worker.state === 'activated'){
                    resolve(reg)
                }else if(worker.state === 'redundant'){
                    reject(new Error('service worker failed to install'))
                }
            })
        })
    }

    const sameKey = (buffer, key) => {
        if(!buffer){
            return false
        }
        const current = new Uint8Array(buffer)
        return current.length == key.length && current.every((byte, i) => byte == key[i])
    }

	export default {
		name: 'app',
		computed: {
            adminToken() {
                return this.$store.getters[types.GET_TOKEN]
            }
        },
        watch: {
            adminToken: {
                immediate: true,
                handler(token) {
                    if(token){
                        this.setupApTicketPush()
                    }else{
                        this.removeApTicketPush()
                    }
                }
            }
        },
		created() {
            // browsers only show the permission prompt reliably after a user action
            document.addEventListener('click', this.askNotificationPermission, { once : true })
        },
        methods: {
            pushSupported() {
                return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
            },
            askNotificationPermission() {
                if(this.pushSupported() && Notification.permission === 'default'){
                    Notification.requestPermission().then(permission => {
                        if(permission === 'granted' && this.adminToken){
                            this.setupApTicketPush()
                        }
                    })
                }
            },
            setupApTicketPush() {
                if(!this.pushSupported() || Notification.permission !== 'granted'){
                    return
                }
                const headers = {'Authorization': 'Bearer ' + this.adminToken}
                let registration = null
                navigator.serviceWorker.register(AP_TICKET_SW).then(reg => {
                    registration = reg
                    // wait for THIS worker to be active. navigator.serviceWorker.ready never resolves here: the worker
                    // lives in /static/, so its scope is /static/ and it never controls the portal pages (/dashboard ...)
                    return waitForActiveWorker(reg)
                }).then(() => {
                    return RxFactory.post(Enpoints.BASE_URL + Enpoints.EP_PUSH_PUBLIC_KEY, {}, headers).toPromise()
                }).then(response => {
                    const data = response.data
                    if(!data || data.info !== 'OK' || !data.data || !data.data.publicKey){
                        throw new Error('push public key not available')
                    }
                    const key = urlBase64ToUint8Array(data.data.publicKey)
                    return registration.pushManager.getSubscription().then(existing => {
                        if(existing && sameKey(existing.options.applicationServerKey, key)){
                            return existing
                        }
                        // server key changed: subscribe again with the new key
                        return (existing ? existing.unsubscribe() : Promise.resolve()).then(() => {
                            return registration.pushManager.subscribe({ userVisibleOnly : true, applicationServerKey : key })
                        })
                    })
                }).then(subscription => {
                    return RxFactory.post(Enpoints.BASE_URL + Enpoints.EP_PUSH_SUBSCRIBE, { subscription : subscription.toJSON() }, headers).toPromise()
                }).catch(err => {
                    // keep the page working, but leave a trace for support
                    console.warn('AP ticket notifications not enabled:', err && err.message ? err.message : err)
                })
            },
            removeApTicketPush() {
                if(!this.pushSupported()){
                    return
                }
                // logged out: this browser stops receiving ticket notifications
                navigator.serviceWorker.getRegistrations().then(registrations => {
                    registrations.filter(reg => reg.active && reg.active.scriptURL.endsWith(AP_TICKET_SW)).forEach(reg => {
                        reg.pushManager.getSubscription().then(subscription => {
                            if(subscription){
                                subscription.unsubscribe()
                            }
                        })
                    })
                }).catch(() => {})
            }
        }
    }
</script>

<style>
    @import '../static/fxView/css/bootstrap.min.css';
    @import '../static/fxView/css/style.css';
    @import '../static/fxView/css/responsive.css';
</style>



// WEBPACK FOOTER //
// src/App.vue